/**
 * Wires the buyer's feedback store (KP-4572) into the branded line sheet: one
 * store per sheet, the heart mirrored into the Phase 1 shortlist, the
 * identify sheet, and Mixpanel events carrying how the buyer is known and
 * nothing they typed.
 *
 * The host provides publicToken, trackedLinkId, plgBaseUrl, shortlist,
 * productsById and wishlist.
 */

import analytics from '@/@core/utils/analytics'
import { createFeedbackApi } from './feedbackApi'
import { createLineSheetFeedback } from './useLineSheetFeedback'
import { storedIdentity } from './visitorIdentity'
import { saveShortlist, withServerLikes } from './shortlist'

export default {
  data() {
    return { identifyBusy: false, identifyError: null }
  },
  computed: {
    feedbackOn() {
      return this.feedback.state.available
    },
    feedbackItems() {
      return this.feedback.state.items
    },
    interestedProducts() {
      return Object.entries(this.feedbackItems)
        .filter(([, item]) => item.interested)
        .map(([id]) => this.productsById[id])
        .filter(Boolean)
    },
    identityInitial() {
      const { viewer } = this.feedback.state
      if (viewer && viewer.via === 'self_reported') return { email: viewer.email, store_name: viewer.store_name }
      return storedIdentity() || {}
    },
  },
  created() {
    // Not in data(): the store keeps its own reactive state and must not be
    // observed a second time.
    this.feedback = createLineSheetFeedback({
      token: this.publicToken,
      trackedLinkId: this.trackedLinkId,
      api: createFeedbackApi({ plgBaseUrl: this.plgBaseUrl, token: this.publicToken }),
      onEvent: this.trackFeedback,
    })
    this.$watch(() => this.feedback.state.items, this.followServerLikes)
  },
  mounted() {
    this.feedback.load()
    window.addEventListener('online', this.onFeedbackOnline)
    window.addEventListener('pagehide', this.onFeedbackLeave)
  },
  beforeDestroy() {
    window.removeEventListener('online', this.onFeedbackOnline)
    window.removeEventListener('pagehide', this.onFeedbackLeave)
    this.feedback.destroy()
  },
  methods: {
    trackFeedback(name, { via }) {
      analytics.track(name, { wishlist_id: (this.wishlist && this.wishlist._id) || '', via, variant: 'branded' })
    },
    followServerLikes(items) {
      const next = withServerLikes(this.shortlist, items)
      if (next === this.shortlist) return
      this.shortlist = next
      saveShortlist(this.publicToken, next)
    },
    feedbackOf(productId) {
      return this.feedback.itemOf(productId)
    },
    // A style the feedback store does not hold yet keeps its shortlist heart,
    // so marking interest or commenting never sends an unlike.
    feedbackPatch(productId, patch) {
      const known = Object.prototype.hasOwnProperty.call(this.feedback.state.items, productId)
      return known ? patch : { liked: this.shortlist.includes(productId), ...patch }
    },
    toggleInterest(productId) {
      this.feedback.setItem(productId, this.feedbackPatch(productId, { interested: !this.feedbackOf(productId).interested }))
    },
    setComment(productId, text) {
      this.feedback.setItem(productId, this.feedbackPatch(productId, { comment: text }))
    },
    async submitIdentity(identity) {
      this.identifyBusy = true
      const out = await this.feedback.identify(identity)
      this.identifyBusy = false
      this.identifyError = out.ok ? null : out
    },
    dismissIdentity() {
      this.identifyError = null
      this.feedback.declineIdentity()
    },
    onFeedbackOnline() {
      this.feedback.online()
    },
    // Sends what is typed when the tab closes; writes use keepalive.
    onFeedbackLeave() {
      this.feedback.commit()
    },
  },
}
